import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'

export function useMentionAutocomplete(textareaRef, projectMembersRef) {
  const showAutocomplete = ref(false)
  const autocompletePosition = ref({ x: 0, y: 0 })
  const selectedIndex = ref(0)
  const mentionQuery = ref('')
  const mentionStartPos = ref(0)
  const activeTextarea = ref(null) // Track the currently active textarea

  // Filter members based on current query
  const filteredMembers = computed(() => {
    // Ensure we're accessing the ref's value correctly for reactivity
    // projectMembersRef should be a ref, so we access .value
    const members = projectMembersRef?.value || []
    const membersArray = Array.isArray(members) ? members : []

    // Filter out invalid members (must have id and username)
    const validMembers = membersArray.filter(member => {
      return member && member.id && member.username
    })

    if (!mentionQuery.value) {
      return validMembers
    }

    const query = mentionQuery.value.toLowerCase()
    const filtered = validMembers.filter(member => {
      const username = (member.username || '').toLowerCase()
      const name = (member.name || '').toLowerCase()
      const matches = username.includes(query) || name.includes(query)
      return matches
    })
    return filtered
  })

  // Handle click outside to close autocomplete
  const handleClickOutside = event => {
    if (showAutocomplete.value) {
      const textarea = textareaRef.value
      const clickedOnTextarea =
        textarea && (event.target === textarea || textarea.contains(event.target))
      const clickedOnAutocomplete = event.target.closest('.mention-autocomplete')

      if (!clickedOnTextarea && !clickedOnAutocomplete) {
        hideAutocomplete()
      }
    }
  }

  // Close autocomplete on scroll (prevents dropdown from being disconnected from textarea)
  const handleScroll = () => {
    if (showAutocomplete.value) {
      hideAutocomplete()
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside, true)
    window.addEventListener('scroll', handleScroll, true)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside, true)
    window.removeEventListener('scroll', handleScroll, true)
  })

  // Get the active textarea - prefer activeTextarea, fallback to ref
  const getActiveTextarea = () => {
    return activeTextarea.value || textareaRef.value
  }

  // Check if @ was typed and update autocomplete state
  const checkForMention = () => {
    const textarea = getActiveTextarea()
    if (!textarea) {
      hideAutocomplete()
      return false
    }

    const cursorPos = textarea.selectionStart
    const textBeforeCursor = textarea.value.substring(0, cursorPos)

    // Find the last @ symbol before cursor
    const lastAtPos = textBeforeCursor.lastIndexOf('@')

    // Check if @ is followed by space, newline, or end of string
    if (lastAtPos === -1) {
      hideAutocomplete()
      return false
    }

    // Check if there's a space or newline between @ and cursor
    const textAfterAt = textBeforeCursor.substring(lastAtPos + 1)
    if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
      hideAutocomplete()
      return false
    }

    // Extract query (text after @)
    const query = textAfterAt.trim()
    mentionQuery.value = query
    mentionStartPos.value = lastAtPos + 1

    // Show autocomplete if we have members to show
    if (filteredMembers.value.length > 0) {
      updateAutocompletePosition(textarea, cursorPos)
      showAutocomplete.value = true
      selectedIndex.value = 0
      return true
    } else {
      hideAutocomplete()
      return false
    }
  }

  // Update autocomplete dropdown position
  const updateAutocompletePosition = (textarea, cursorPos) => {
    const rect = textarea.getBoundingClientRect()
    const dropdownMaxHeight = 200 // Approximate max height of dropdown
    const gap = 4

    // Calculate available space above and below
    const spaceBelow = window.innerHeight - rect.bottom - gap
    const spaceAbove = rect.top - gap

    // Position dropdown aligned to the left of textarea
    const x = Math.max(8, Math.min(rect.left, window.innerWidth - 220))

    // Decide whether to position above or below
    const positionAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow

    let y
    if (positionAbove) {
      // Position above: anchor bottom edge just above the textarea
      // y represents where the bottom of the dropdown should be
      y = rect.top - gap
    } else {
      // Position below: anchor top edge just below the textarea
      y = rect.bottom + gap
    }

    autocompletePosition.value = { x, y, positionAbove }
  }

  // Insert selected mention
  const insertMention = member => {
    const textarea = getActiveTextarea()
    if (!textarea || !member || !member.id || !member.username) return

    const username = member.username

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const textBefore = textarea.value.substring(0, mentionStartPos.value - 1) // Before @
    const textAfter = textarea.value.substring(end)

    const newValue = textBefore + `@${username} ` + textAfter
    const newCursorPos = textBefore.length + username.length + 2 // After @username and space

    // Update textarea value
    textarea.value = newValue

    // Trigger input event for v-model
    const inputEvent = new InputEvent('input', {
      bubbles: true,
      cancelable: true,
      inputType: 'insertText',
    })
    textarea.dispatchEvent(inputEvent)

    const changeEvent = new Event('change', { bubbles: true })
    textarea.dispatchEvent(changeEvent)

    // Set cursor position
    nextTick(() => {
      textarea.setSelectionRange(newCursorPos, newCursorPos)
      textarea.focus()
    })

    hideAutocomplete()
  }

  // Handle keyboard navigation
  const handleKeyDown = event => {
    // Store the active textarea from the event target
    if (event.target && event.target.tagName === 'TEXTAREA') {
      activeTextarea.value = event.target
    }

    // If @ was typed, check for mention after it's inserted
    if (!showAutocomplete.value && event.key === '@') {
      // Let @ be inserted first, then check
      setTimeout(() => checkForMention(), 10)
      return false
    }

    if (!showAutocomplete.value) {
      return false
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        event.stopPropagation()
        selectedIndex.value = Math.min(selectedIndex.value + 1, filteredMembers.value.length - 1)
        return true

      case 'ArrowUp':
        event.preventDefault()
        event.stopPropagation()
        selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
        return true

      case 'Enter':
      case 'Tab':
        event.preventDefault()
        event.stopPropagation()
        if (filteredMembers.value[selectedIndex.value]) {
          insertMention(filteredMembers.value[selectedIndex.value])
        }
        return true

      case 'Escape':
        event.preventDefault()
        event.stopPropagation()
        hideAutocomplete()
        return true

      case 'Backspace':
        // Re-check mention after backspace
        setTimeout(() => checkForMention(), 0)
        break

      default:
        // For typing, re-check mention
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
          setTimeout(() => checkForMention(), 0)
        }
        break
    }

    return false
  }

  // Handle input event (for v-model changes)
  const handleInput = event => {
    // Store the active textarea from the event target
    if (event && event.target && event.target.tagName === 'TEXTAREA') {
      activeTextarea.value = event.target
    }
    // Always check for mentions on input, not just when autocomplete is showing
    checkForMention()
  }

  const hideAutocomplete = () => {
    showAutocomplete.value = false
    selectedIndex.value = 0
    mentionQuery.value = ''
  }

  return {
    showAutocomplete,
    autocompletePosition,
    filteredMembers,
    selectedIndex,
    mentionQuery,
    checkForMention,
    handleKeyDown,
    handleInput,
    insertMention,
    hideAutocomplete,
  }
}
